// Shared constants + pure helpers for the Safety Calendar tile.
// Severities are a small fixed set with a stable key so logic can rank them
// (red/Serious is worst). Colors use the platform's Tailwind palette.

export const SEVERITY = {
  green:  { label: 'No Issues', code: 'OK', rank: 1, dot: 'bg-emerald-500', tint: 'bg-emerald-500/15', bar: 'bg-emerald-500', text: 'text-emerald-400' },
  yellow: { label: 'Minor',     code: 'L1', rank: 2, dot: 'bg-yellow-400',  tint: 'bg-yellow-400/15',  bar: 'bg-yellow-400',  text: 'text-yellow-300' },
  orange: { label: 'Moderate',  code: 'L2', rank: 3, dot: 'bg-orange-500',  tint: 'bg-orange-500/15',  bar: 'bg-orange-500',  text: 'text-orange-300' },
  red:    { label: 'Serious',   code: 'L3', rank: 4, dot: 'bg-red-600',     tint: 'bg-red-600/20',     bar: 'bg-red-600',     text: 'text-red-400' },
};

export const SEVERITY_ORDER = ['green', 'yellow', 'orange', 'red'];

export const OSHA_CATEGORY = 'OSHA Reportable Injury';

export const SERIOUS_ONLY_CATEGORIES = ['OSHA Reportable Injury', 'Non-OSHA Reportable Injury'];
export const BASE_CATEGORIES = [
  'Safety Issue/Callout',
  'Damage',
  'Near-Miss/Policy Violation',
  'Minor Injury',
];

export function categoriesForSeverity(severity) {
  return severity === 'red' ? SERIOUS_ONLY_CATEGORIES : BASE_CATEGORIES;
}

export const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
export const WEEKDAYS = ['SUN','MON','TUE','WED','THU','FRI','SAT'];

// ---- date helpers (local calendar days, no time component) ----

export function formatDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function parseDateKey(key) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

// 42-cell month grid including de-emphasized leading/trailing days.
export function getMonthDays(date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const lastDay = new Date(year, month + 1, 0);
  const days = [];
  const startWeekday = new Date(year, month, 1).getDay();
  for (let i = startWeekday - 1; i >= 0; i--) {
    days.push({ date: new Date(year, month, -i), current: false });
  }
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), current: true });
  }
  while (days.length < 42) {
    const last = days[days.length - 1].date;
    const next = new Date(last);
    next.setDate(next.getDate() + 1);
    days.push({ date: next, current: false });
  }
  return days;
}

export function worstSeverityOf(dayEntries) {
  if (!dayEntries || dayEntries.length === 0) return null;
  return dayEntries.reduce((worst, e) => {
    const r = SEVERITY[e.severity]?.rank || 0;
    return r > (SEVERITY[worst]?.rank || 0) ? e.severity : worst;
  }, dayEntries[0].severity);
}

// Local wall-clock time from a stored UTC ISO string, short form.
export function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US');
}
